import { FieldState } from 'formstate';
import { observer } from 'mobx-react';
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@components/ui/card';
import { Label } from '@components/ui/label';
import { Switch } from '@components/ui/switch';
import { cn } from '@core/utils/cn';
import { TenantCorporateContext } from '..';
import { Spinner } from '../../components/spinner';
import { CorporateFeatureDto } from '../../core/api/CorporateClient';
import { FeatureType, SubFeatureType, useStore } from './store';

function checkIfAppFeature(feature: string) {
    return ['STRAVA', 'GARMIN', 'FITBIT', 'POLAR'].includes(feature);
}

const TenantFeatures = observer(() => {
    const corporateClient = React.useContext(TenantCorporateContext);
    const {
        form,
        corporateFeatures,
        enableFeatureRequest,
        disableFeatureRequest,
        enableFitnessApps
    } = useStore(corporateClient);
    const [loadingFeature, setLoadingFeature] = React.useState<boolean>(false);

    async function handleFeatureToggle(
        field: FieldState<boolean>,
        feature: CorporateFeatureDto['feature']
    ) {
        setLoadingFeature(true);

        if (field.value) {
            await disableFeatureRequest.request([feature]);
        } else {
            if (checkIfAppFeature(feature) && field) {
                await enableFitnessApps();
            }
            await enableFeatureRequest.request([feature]);
        }
        field.onChange(!field.value);
        setLoadingFeature(false);
        return;
    }

    return (
        <>
            {corporateFeatures.match({
                loading: () => (
                    <div className="flex min-h-[400px] items-center justify-center">
                        <Spinner />
                    </div>
                ),
                fetched: () => (
                    <div className="grid items-start gap-4 md:grid-cols-2 xl:grid-cols-3">
                        {form.current?.$.map((group) => {
                            const {
                                $: { name, id, features }
                            } = group;
                            return (
                                <Card key={id.value}>
                                    <CardHeader>
                                        <CardTitle className="text-xs uppercase tracking-wide text-muted-foreground">
                                            {name.value}
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent>
                                        <ul className="divide-y divide-border">
                                            {features.$.map((feature) => (
                                                <React.Fragment
                                                    key={feature.$.id.value}
                                                >
                                                    <FeatureToggle
                                                        feature={feature}
                                                        level={0}
                                                        loading={loadingFeature}
                                                        handleFeatureToggle={
                                                            handleFeatureToggle
                                                        }
                                                    />
                                                    {feature.$.subFeatures.$.map(
                                                        (subFeature) => (
                                                            <FeatureToggle
                                                                key={
                                                                    subFeature.$
                                                                        .id
                                                                        .value
                                                                }
                                                                feature={
                                                                    subFeature
                                                                }
                                                                level={1}
                                                                loading={
                                                                    loadingFeature
                                                                }
                                                                handleFeatureToggle={
                                                                    handleFeatureToggle
                                                                }
                                                            />
                                                        )
                                                    )}
                                                </React.Fragment>
                                            ))}
                                        </ul>
                                    </CardContent>
                                </Card>
                            );
                        }) || <></>}
                    </div>
                )
            })}
        </>
    );
});

interface FeatureToggleProps {
    feature: SubFeatureType | FeatureType;
    level: 0 | 1;
    handleFeatureToggle(
        field: FieldState<boolean>,
        feature: CorporateFeatureDto['feature']
    ): void;
    loading: boolean;
}
const FeatureToggle = ({
    feature,
    loading,
    level,
    handleFeatureToggle
}: FeatureToggleProps) => {
    const id = `feature-${feature.$.id.value}`;
    return (
        <li
            className={cn(
                'flex items-center justify-between gap-3 py-2',
                level === 1 && 'pl-6'
            )}
        >
            <Label htmlFor={id} className="font-normal">
                {feature.$.name.value}
            </Label>
            <Switch
                id={id}
                checked={feature.$.enabled.value}
                onCheckedChange={() => {
                    handleFeatureToggle(feature.$.enabled, feature.$.id.value);
                }}
                name={feature.$.id.value}
                disabled={loading}
            />
        </li>
    );
};

export default TenantFeatures;
